import React, { useCallback, useState, useRef } from 'react';
import { Camera, Image as ImageIcon } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
interface ImageUploadProps {
  value?: string;
  onChange: (url: string) => void;
  onError?: (error: string) => void;
  error?: string;
  className?: string;
  /**
   * When true, renders a mobile-only pair of buttons below the drop zone:
   * "Fă un selfie" (front camera) and "Alege din galerie".
   * Desktop is unaffected — the drop zone keeps its click-to-upload behavior.
   */
  showCameraOption?: boolean;
}
export function ImageUpload({
  value,
  onChange,
  onError,
  error,
  className,
  showCameraOption = false
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(value || null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const handleFile = useCallback(async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      onError?.('Format invalid. Folosește JPEG, PNG, WebP sau AVIF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      onError?.('Fișierul este prea mare. Maxim 10MB.');
      return;
    }
    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = e => {
      const dataUrl = e.target?.result as string;
      setPreview(dataUrl);
      setTimeout(() => {
        onChange(dataUrl);
        setIsUploading(false);
      }, 500);
    };
    reader.onerror = () => {
      onError?.('Eroare la citirea fișierului.');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  }, [onChange, onError]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset the input so selecting the same file again still fires onChange
    e.target.value = '';
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  return <div className={cn('space-y-3 w-full', className)} data-id="element-1303">
      <div onClick={() => inputRef.current?.click()} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={cn('relative flex flex-col items-center justify-center w-full h-48 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200', isDragOver ? 'border-lime-1 bg-lime-5 scale-[1.02]' : 'border-dark-3 hover:border-dark-4 hover:bg-dark-2', error && 'border-red-1', isUploading && 'pointer-events-none opacity-70')} data-id="element-1304">
        {isUploading ? <div className="flex flex-col items-center gap-2" data-id="element-1305">
            <div className="w-8 h-8 border-2 border-lime-1 border-t-transparent rounded-full animate-spin" data-id="element-1306" />
            <span className="text-sm text-light-3" data-id="element-1307">Se încarcă...</span>
          </div> : preview ? <div className="relative w-full h-full p-2" data-id="element-1308">
            <img src={preview} alt="Preview" className="w-full h-full object-contain rounded-lg" data-id="element-1309" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-dark-1/60 rounded-lg transition-opacity duration-200" data-id="element-1310">
              <span className="text-light-1 text-sm font-medium" data-id="element-1311">
                Schimbă poza
              </span>
            </div>
          </div> : <div className="flex flex-col items-center gap-2 px-4 text-center" data-id="element-1312">
            <div className="w-12 h-12 rounded-xl bg-dark-3 flex items-center justify-center" data-id="element-1313">
              <ImageIcon className="w-6 h-6 text-light-3" weight="regular" data-id="element-1314" />
            </div>
            <div data-id="element-1315">
              <p className="text-sm font-medium text-light-1 sm:hidden" data-id="element-1316">
                Adaugă o poză
              </p>
              <p className="hidden sm:block text-sm font-medium text-light-1" data-id="element-1317">
                Trage poza aici sau apasă pentru a alege
              </p>
              <p className="text-xs text-light-3 mt-1" data-id="element-1318">
                JPEG, PNG, WebP sau AVIF • Max 10MB
              </p>
            </div>
          </div>}

        <input ref={inputRef} type="file" accept={ACCEPTED_TYPES.join(',')} onChange={handleInputChange} className="hidden" aria-label="Încarcă poza de profil" data-id="element-1319" />
      </div>

      {/* Mobile-only: camera + gallery buttons */}
      {showCameraOption && !preview && <div className="grid grid-cols-2 gap-2 sm:hidden" data-id="element-1320">
          <button type="button" onClick={e => {
        e.stopPropagation();
        cameraInputRef.current?.click();
      }} className="inline-flex items-center justify-center gap-2 h-11 rounded-md bg-dark-2 border border-dark-3 text-sm font-medium text-light-1 hover:bg-dark-3 transition-colors" data-id="element-1321">
            <Camera className="w-4 h-4" weight="bold" data-id="element-1322" />
            Fă un selfie
          </button>
          <button type="button" onClick={e => {
        e.stopPropagation();
        galleryInputRef.current?.click();
      }} className="inline-flex items-center justify-center gap-2 h-11 rounded-md bg-dark-2 border border-dark-3 text-sm font-medium text-light-1 hover:bg-dark-3 transition-colors" data-id="element-1323">
            <ImageIcon className="w-4 h-4" weight="bold" data-id="element-1324" />
            Din galerie
          </button>
          {/* Hidden camera input — opens front camera on mobile */}
          <input ref={cameraInputRef} type="file" accept="image/*" capture="user" onChange={handleInputChange} className="hidden" aria-label="Fă o poză cu camera" data-id="element-1325" />
          <input ref={galleryInputRef} type="file" accept={ACCEPTED_TYPES.join(',')} onChange={handleInputChange} className="hidden" aria-label="Alege din galerie" data-id="element-1326" />
        </div>}

      {error && <p className="text-xs text-red-1" data-id="element-1327">{error}</p>}
    </div>;
}